/*!
 * gara-ymm-filter.js — canonical YMM filter for all Gara YMM widgets.
 *
 * Built 2026-05-22 as part of the System 1 fitment unification refactor.
 * Replaces the per-widget ~500-line filtering logic that previously lived
 * inside gara-bike-finder.liquid, gara-tire-finder.liquid,
 * gara-oil-kit-builder.liquid, gara-oil-kit-finder.liquid.
 *
 * ─── DATA CONTRACTS ────────────────────────────────────────────────────
 *
 * 1. localStorage.gara_ymm  — JSON payload {"year":2024,"make":"Yamaha","model":"MT-09"}
 *    or absent when no bike is selected. The same key is shared across all
 *    four YMM widgets so the rider's bike persists site-wide.
 *    `slug` ("kawasaki/z900") is the stable identity key and is what every
 *    match path prefers. A payload stored without one is repaired from the
 *    bike registry wherever it is loaded, see "stale bike slug healing".
 *
 * 2. .gara-pcard[data-fitment]  — emitted by gara-product-card.liquid:54
 *    Format: semicolon-separated rows of "<make-slug>|<model-slug>|<y1>|<y2>;"
 *    Special values:
 *      "UNIVERSAL"  → fits any bike
 *      ""           → no metafield data (treated as NO_DATA, see classify())
 *
 *    Slugs: lowercase, spaces → hyphens, "/" → "-". The Liquid does this
 *    transformation; we mirror it in slugify() below.
 *
 * 3. document event "gara-ymm:change"  — dispatched on set()/clear().
 *    detail = {year, make, model} | null. Widgets listen to refresh their UI.
 *
 * ─── PUBLIC API ────────────────────────────────────────────────────────
 *
 *    GaraYMM.get()                  → {year, make, model} | null
 *    GaraYMM.set({year, make, model})
 *    GaraYMM.clear()
 *    GaraYMM.subscribe(fn)          → returns unsubscribe fn
 *    GaraYMM.classify(card)         → 'match' | 'universal' | 'no_data' | 'no_match'
 *    GaraYMM.filterGrid(opts)       → applies show/hide + returns counts
 *                                       opts = {cards?, ymm?, hideNoData?: true}
 *
 * Widgets call filterGrid() once when YMM changes; the helper handles all
 * the per-card matching. Widgets are responsible for their own UI (chip
 * labels, "X parts fit your bike" copy, near-match messages, etc.).
 */
(function (global) {
  'use strict';

  var STORAGE_KEY = 'gara_ymm';
  var EVT = 'gara-ymm:change';

  // ─── helpers ─────────────────────────────────────────────────────────

  function slugify(s) {
    if (s == null) return '';
    return String(s).toLowerCase().trim()
      .replace(/\s+/g, '-')
      .replace(/\//g, '-')
      // Collapse runs of hyphens and trim edge hyphens so a display name like
      // "Boulevard C90 / Intruder 1500LC" (which would otherwise yield
      // "boulevard-c90---intruder-1500lc") matches the index's single-hyphen
      // slug. MUST stay identical to canonical/index_builder._slug.
      .replace(/-+/g, '-')
      .replace(/^-+|-+$/g, '');
  }

  // ─── Model-name canonicalization ─────────────────────────────────────
  //
  // System 5 v2 (2026-05-25): the dropdown sends canonical bike strings
  // (e.g., 'MT-09 SP', 'CBR1000RR-R Fireblade SP') and the INDEX stores
  // the same canonical slugs (canonicalize_against_dropdown matches every
  // catalog row to one of the dropdown's 634 (make, model) pairs at
  // index-build time). So this JS side ONLY needs to slugify — strict
  // equality matches the index.
  //
  // This intentionally avoids any client-side heuristic normalization.
  // If a dropdown selection doesn't match an index entry, the bug is
  // either: (a) the dropdown gained a bike the catalog doesn't carry,
  // or (b) the catalog has fitment data that didn't normalize via the
  // server-side matcher (look at canonical/bike_dropdown.py to extend).
  function canonicalizeModelSlug(s) {
    return slugify(s);
  }

  // Canonical [make_slug, model_slug] for matching the product index + spine.
  // PREFERS the explicit `slug` the dropdown now carries (gara-bike-registry-data.js),
  // which decouples the display name from the join key and fixes the ~115 bikes whose
  // display does NOT slugify to their canonical slug (e.g. "RS4 125" displays but the
  // parts/spec live under aprilia/125-rs4). Falls back to slugifying make/model for any
  // bike or older session without a slug, so the ~6,123 already-matching bikes are
  // unchanged (their slugify == slug by construction).
  function ymmSlugPair(ymm, modelFn) {
    // Every match path funnels through here, both classifyViaIndex and the
    // data-fitment fallback, so this is where a stored bike that predates
    // the `slug` field gets repaired. See "stale bike slug healing" below.
    // Costs one property read for the bikes that already carry a slug, and
    // healStaleSlug re-checks nothing once it has tried a given bike.
    if (ymm && !ymm.slug) healStaleSlug(ymm);
    if (ymm && ymm.slug && ymm.slug.indexOf('/') > 0) {
      var i = ymm.slug.indexOf('/');
      return [ymm.slug.slice(0, i), ymm.slug.slice(i + 1)];
    }
    return [slugify(ymm.make), (modelFn || slugify)(ymm.model)];
  }

  function parseFitment(raw) {
    // Returns one of:
    //   {kind:'universal'}
    //   {kind:'no_data'}
    //   {kind:'rows', rows: [{make, model, y1, y2}, ...]}
    if (raw == null) return {kind: 'no_data'};
    var s = String(raw).trim();
    if (!s) return {kind: 'no_data'};
    if (s === 'UNIVERSAL') return {kind: 'universal'};
    var rows = [];
    var parts = s.split(';');
    for (var i = 0; i < parts.length; i++) {
      var p = parts[i].trim();
      if (!p) continue;
      var f = p.split('|');
      if (f.length !== 4) continue;
      var y1 = parseInt(f[2], 10);
      var y2 = parseInt(f[3], 10);
      if (!isFinite(y1)) y1 = 0;
      if (!isFinite(y2) || y2 < y1) y2 = y1;
      rows.push({make: f[0], model: f[1], y1: y1, y2: y2});
    }
    if (!rows.length) return {kind: 'no_data'};
    return {kind: 'rows', rows: rows};
  }

  // ─── state ───────────────────────────────────────────────────────────

  function get() {
    try {
      var raw = global.localStorage.getItem(STORAGE_KEY);
      if (!raw) return null;
      var v = JSON.parse(raw);
      if (!v || !v.year || !v.make || !v.model) return null;
      var out = {year: Number(v.year), make: String(v.make), model: String(v.model)};
      if (v.slug) out.slug = String(v.slug);   // stable identity key (decouples display name)
      // Preserve the bike's resolved stock tire sizes when present so every
      // pair suggester is bike-spec aware (front ≠ rear; ADV/MX differ by wheel).
      if (v.front)     out.front = v.front;
      if (v.rear)      out.rear = v.rear;
      if (v.front_alt) out.front_alt = v.front_alt;
      if (v.rear_alt)  out.rear_alt = v.rear_alt;
      if (v.front_rating) out.front_rating = v.front_rating;   // OE service rating
      if (v.rear_rating)  out.rear_rating = v.rear_rating;
      return out;
    } catch (_) { return null; }
  }

  // ─── bike → stock tire size resolution ───────────────────────────────
  //
  // GaraYMM is the SINGLE owner of "what tires does this bike take". It reads
  // the full GARA_TIRE_FINDER.bikes when present (tire collection pages) and
  // otherwise the compact site-wide GARA_BIKE_SIZES (loaded in theme.liquid),
  // so the rider's stock front/rear sizes resolve on EVERY surface — cart
  // drawer + PDP "Complete the pair" — no matter which widget set the bike.
  // Both data shapes are normalized to {f, r, fa, ra, mk, md, ys, ye} here.

  function bikeRows() {
    var F = global.GARA_TIRE_FINDER;
    if (F && F.bikes && F.bikes.length) {
      var rows = [];
      for (var i = 0; i < F.bikes.length; i++) {
        var b = F.bikes[i];
        rows.push({ys: b.year_start, ye: b.year_end, mk: b.make, md: b.model,
                   f: b.front, r: b.rear, fa: b.front_alt, ra: b.rear_alt,
                   fr: b.front_rating, rr: b.rear_rating});
      }
      return rows;
    }
    var C = global.GARA_BIKE_SIZES;
    return (C && C.length) ? C : [];
  }

  function ciEq(a, b) {
    return String(a == null ? '' : a).toLowerCase().trim() ===
           String(b == null ? '' : b).toLowerCase().trim();
  }

  function resolveSizes(ymm) {
    // → {front, rear, front_alt?, rear_alt?} | null
    if (!ymm || !ymm.make || !ymm.model) return null;
    var yr = Number(ymm.year) || 0;
    var rows = bikeRows();
    var best = null, bestYs = -1;
    // The bike DB has overlapping year ranges for a model (e.g. several R1 rows
    // all ending 2026 as the OE size changed). Among rows matching make+model
    // AND containing the selected year, pick the one with the LATEST year_start
    // — the most specific/recent spec applicable to that year (so a 2015 R1
    // resolves to 200/55, not the legacy 190/50 from a broad 1998-2026 row).
    for (var i = 0; i < rows.length; i++) {
      var b = rows[i];
      if (!ciEq(b.mk, ymm.make) || !ciEq(b.md, ymm.model)) continue;
      var ys = Number(b.ys) || 0, ye = Number(b.ye) || ys;
      if (yr && (yr < ys || yr > ye)) continue;
      if (ys >= bestYs) { best = b; bestYs = ys; }
    }
    if (!best) {                  // fallback: make+model, any year row
      for (var j = 0; j < rows.length; j++) {
        var c = rows[j];
        if (ciEq(c.mk, ymm.make) && ciEq(c.md, ymm.model)) { best = c; break; }
      }
    }
    if (!best) return null;
    var out = {};
    if (best.f)  out.front = best.f;
    if (best.r)  out.rear = best.r;
    if (best.fa) out.front_alt = best.fa;
    if (best.ra) out.rear_alt = best.ra;
    if (best.fr) out.front_rating = best.fr;   // OE service rating (load+speed)
    if (best.rr) out.rear_rating = best.rr;
    return (out.front || out.rear) ? out : null;
  }

  // Spine-first: if the stored bike already carries front/rear sizes (the spec
  // sheet passes them straight from the SPINE — spec.tire, by_year/gen aware),
  // trust THOSE. Only fall back to resolveSizes (the compact GARA_BIKE_SIZES
  // projection) when no spine sizes were handed in — e.g. a bike picked in the
  // tire-finder dropdown rather than carried from its spec sheet. This keeps the
  // spine as the single source and stops the parallel list from overriding it.
  function getTireSizes() {
    var v = get();
    if (v && (v.front || v.rear)) {
      var fr = v.front_rating || null, rr = v.rear_rating || null;
      // A bike stored before the OE-rating field existed has sizes but no rating.
      // Backfill the rating from the compact DB so the picker still warns/defaults
      // correctly without forcing a re-select. Sizes stay the stored (spine) ones.
      if (!fr && !rr) {
        var d = resolveSizes(v);
        if (d) { fr = d.front_rating || null; rr = d.rear_rating || null; }
      }
      return { front: v.front || null, rear: v.rear || null,
               front_alt: v.front_alt || null, rear_alt: v.rear_alt || null,
               front_rating: fr, rear_rating: rr };
    }
    return resolveSizes(v);
  }

  // ─── stale bike slug healing ─────────────────────────────────────────
  //
  // A bike saved before the dropdown carried `slug`, or saved by a surface
  // that still does not pass one, holds only {year, make, model}. Matching
  // then falls back to slugify(display), and for a TRIM display name that
  // key exists nowhere: "Z900 ABS" slugifies to "z900-abs" while every part
  // for that bike lives under the platform slug "kawasaki/z900". The rider
  // sees zero fitting parts and nothing tells them why. 1,742 of the
  // registry's 3,017 rows are trim names whose display does not slugify to
  // their slug, so this is most of the catalog, not an edge case.
  //
  // Heal means re-derive the canonical slug from the registry and stamp it.
  // The registry asset only loads on surfaces that render a bike or tire
  // finder, plus lazily when the nav's MY BIKE strip is first opened, so we
  // try at init, again at the first classification of a bike with no slug,
  // and once more inside set(). Pages with no registry behave exactly as
  // they do today.
  //
  // Conservative on purpose: exactly one slug or nothing. 11 of the
  // registry's 17,919 (bike, year) combinations genuinely resolve to two
  // platforms (Triumph Street Triple 675 R, Triumph Tiger 1050, Beta Alp
  // 4.0) and those keep the slugify fallback, because a WRONG slug shows
  // the rider parts that do not fit their bike. A stored bike is never
  // cleared here, only ever given a slug it was missing.

  var _healKey = null;   // bike identity already attempted this page load

  function registryBikes() {
    var R = global.GARA_BIKE_REGISTRY;
    return (R && R.bikes && R.bikes.length) ? R.bikes : null;
  }

  function registrySlug(ymm) {
    // Canonical "make/model" slug for this bike, or null when the registry
    // is not loaded, the display name is not in it, or it resolves to more
    // than one platform.
    try {
      var rows = registryBikes();
      if (!rows || !ymm) return null;
      var yr = Number(ymm.year) || 0;
      var found = null;
      for (var i = 0; i < rows.length; i++) {
        var b = rows[i];
        if (!b || !b.slug) continue;                        // malformed row
        if (!ciEq(b.make, ymm.make) || !ciEq(b.model, ymm.model)) continue;
        var ys = Number(b.year_start) || 0;
        var ye = Number(b.year_end) || ys;
        if (yr && ys && (yr < ys || yr > ye)) continue;      // wrong generation
        var slug = String(b.slug);
        if (slug.indexOf('/') <= 0) continue;               // not a make/model key
        if (found && found !== slug) return null;           // ambiguous, leave it alone
        found = slug;
      }
      return found;
    } catch (_) { return null; }
  }

  function healStaleSlug(ymm) {
    // Stamp the canonical slug on a bike that has none: in memory first, so
    // the filtering pass running right now matches on it, then persisted
    // through set(), the one write path. The persist only fires when this
    // bike IS the stored bike, so a widget classifying some other bike can
    // never overwrite what the rider picked.
    try {
      if (!ymm || ymm.slug || !ymm.year || !ymm.make || !ymm.model) return;
      var key = String(ymm.year) + '|' + String(ymm.make).toLowerCase() +
                '|' + String(ymm.model).toLowerCase();
      if (key === _healKey) return;      // already tried this bike this load
      if (!registryBikes()) return;      // no registry here, retry where there is one
      _healKey = key;                    // registry WAS present, one scan is enough
      var slug = registrySlug(ymm);
      if (!slug) return;                 // unknown or ambiguous, keep the fallback
      ymm.slug = slug;
      var stored = get();
      if (!stored || stored.slug) return;
      if (Number(stored.year) !== Number(ymm.year)) return;
      if (!ciEq(stored.make, ymm.make) || !ciEq(stored.model, ymm.model)) return;
      stored.slug = slug;
      // Deferred: set() fires gara-ymm:change and we may be part way through
      // a widget's card loop right now. Next tick keeps that loop whole and
      // still lands long before the rider can click anything.
      global.setTimeout(function () { try { set(stored); } catch (_) {} }, 0);
    } catch (_) {}
  }

  function writeCartAttrs(payload) {
    // Mirror the bike's stock sizes into cart attributes so the cart-drawer
    // suggester can pre-pick the matching size on its SERVER render (first
    // paint), before the client swap runs. Fire-and-forget.
    try {
      global.fetch('/cart/update.js', {
        method: 'POST', credentials: 'same-origin',
        headers: {'Content-Type': 'application/json'},
        body: JSON.stringify({attributes: {
          ymm_front: (payload && payload.front) || '',
          ymm_rear:  (payload && payload.rear)  || ''
        }})
      }).catch(function () {});
    } catch (_) {}
  }

  function set(ymm) {
    if (!ymm || !ymm.year || !ymm.make || !ymm.model) {
      return clear();
    }
    var payload = {
      year: Number(ymm.year),
      make: String(ymm.make),
      model: String(ymm.model),
    };
    if (ymm.slug) payload.slug = String(ymm.slug);   // carry the stable identity key site-wide
    else {
      // Heal at the WRITE too. The nav's MY BIKE strip (gara-mega-nav) and
      // the tire finder still call set() with a bare {year, make, model}, so
      // a bike picked there is BORN into the broken slugify(display) path.
      // Silent no-op when the registry is not on the page, or when the name
      // is ambiguous.
      var healed = registrySlug(payload);
      if (healed) payload.slug = healed;
    }
    // Carry the bike's stock tire sizes. Use caller-provided sizes when given
    // (the tire-finder already knows them); otherwise resolve from the DB.
    var sizes = (ymm.front || ymm.rear)
      ? {front: ymm.front, rear: ymm.rear, front_alt: ymm.front_alt, rear_alt: ymm.rear_alt,
         front_rating: ymm.front_rating, rear_rating: ymm.rear_rating}
      : resolveSizes(payload);
    if (sizes) {
      if (sizes.front)     payload.front = sizes.front;
      if (sizes.rear)      payload.rear = sizes.rear;
      if (sizes.front_alt) payload.front_alt = sizes.front_alt;
      if (sizes.rear_alt)  payload.rear_alt = sizes.rear_alt;
      if (sizes.front_rating) payload.front_rating = sizes.front_rating;   // OE rating
      if (sizes.rear_rating)  payload.rear_rating = sizes.rear_rating;
    }
    try {
      global.localStorage.setItem(STORAGE_KEY, JSON.stringify(payload));
    } catch (_) { /* quota / private mode */ }
    writeCartAttrs(payload);
    fire(payload);
  }

  function clear() {
    try { global.localStorage.removeItem(STORAGE_KEY); } catch (_) {}
    writeCartAttrs(null);
    fire(null);
  }

  // One-time backfill: a bike stored before this change (older session, or set
  // via the bike-finder which only wrote y/m/m) carries no sizes. Resolve +
  // persist once so the suggesters become bike-spec aware without re-selecting.
  // Deferred to DOMContentLoaded so the size DB asset is loaded first.
  function backfillSizes() {
    try {
      var raw = global.localStorage.getItem(STORAGE_KEY);
      if (!raw) return;
      var v = JSON.parse(raw);
      if (!v || !v.year || !v.make || !v.model) return;
      if (v.front || v.rear) return;            // already has sizes
      var sizes = resolveSizes(v);
      if (!sizes) return;
      if (sizes.front)     v.front = sizes.front;
      if (sizes.rear)      v.rear = sizes.rear;
      if (sizes.front_alt) v.front_alt = sizes.front_alt;
      if (sizes.rear_alt)  v.rear_alt = sizes.rear_alt;
      global.localStorage.setItem(STORAGE_KEY, JSON.stringify(v));
      writeCartAttrs(v);
      fire(v);
    } catch (_) {}
  }

  function fire(payload) {
    try {
      global.document.dispatchEvent(new CustomEvent(EVT, {detail: payload}));
    } catch (_) {
      // Older browsers: fall through silently.
    }
  }

  function subscribe(fn) {
    var handler = function (e) { fn(e.detail); };
    global.document.addEventListener(EVT, handler);
    return function () { global.document.removeEventListener(EVT, handler); };
  }

  // ─── matching ────────────────────────────────────────────────────────
  //
  // System 5 v2 (2026-05-25): primary lookup path is the materialized
  // index at window.GARA_PRODUCT_INDEX[sku] — a compact dict produced by
  // canonical/index_builder.py from canonical_store.jsonl. The sealed
  // fitment union (universal / fitment_rows / caliper_only /
  // bike_specific_unknown / no_data) ends the old "fitment in title but
  // YMM wrong" bug class because there's now ONE source of truth.
  //
  // Transition fallback: if the index isn't loaded yet (or the sku
  // isn't in it), fall back to the per-card data-fitment attribute that
  // the legacy gara-product-card.liquid still emits. Phase 6 removes
  // the data-fitment fallback once every card path is index-backed.

  function classifyViaIndex(card, ymm) {
    // Returns 'match' | 'universal' | 'no_data' | 'no_match' | null
    // null means "index doesn't cover this sku — caller falls back".
    var sku = card && card.getAttribute ? card.getAttribute('data-sku') : null;
    if (!sku) return null;
    var IDX = global.GARA_PRODUCT_INDEX;
    var entry = (IDX && typeof IDX === 'object') ? IDX[sku] : null;
    if (!entry) {
      // FITMENT SLICE (2026-09-18): on a sharded collection the shard carries
      // the index's verdict for its own SKUs (gara-collection-cards.js registers
      // window.GARA_INDEX_SLICE at load), so the grid is judged before the full
      // 7.4 MB index arrives (that one is async there). Same verdict object, so
      // slice and index can never disagree; the offline shard gate asserts it.
      var S = global.GARA_INDEX_SLICE;
      entry = (S && typeof S === 'object') ? S[sku] : null;
    }
    if (!entry || !entry.f) return null;
    var f = entry.f;

    if (f.k === 'universal') return 'universal';
    if (f.k === 'no_data') return 'no_data';
    if (f.k === 'bike_specific_unknown') return 'no_data';   // hide for safety
    if (f.k === 'caliper_only') {
      // CaliperOnly products HIDE from YMM-filtered grids; the PDP shows
      // a compatibility banner. Treat as no_data for filter purposes.
      return 'no_data';
    }
    if (f.k !== 'rows' || !f.r) return null;   // unknown variant → fall through

    if (!ymm) return 'universal';
    var _p = ymmSlugPair(ymm, canonicalizeModelSlug);
    var pickedMake  = _p[0];
    var pickedModel = _p[1];   // canonical, matches index (prefers explicit slug)
    var pickedYear  = Number(ymm.year);

    for (var i = 0; i < f.r.length; i++) {
      var row = f.r[i];   // [make_slug, model_slug, y1, y2]
      if (row[0] !== pickedMake) continue;
      if (row[1] !== pickedModel) continue;
      if (pickedYear && (pickedYear < row[2] || pickedYear > row[3])) continue;
      return 'match';
    }
    return 'no_match';
  }

  function classify(card, ymm) {
    // Returns 'match' | 'universal' | 'no_data' | 'no_match'
    // ymm is optional; defaults to current localStorage value.
    if (ymm === undefined) ymm = get();

    // Primary: GARA_PRODUCT_INDEX lookup by sku.
    var viaIndex = classifyViaIndex(card, ymm);
    if (viaIndex !== null) return viaIndex;

    // Fallback: legacy per-card data-fitment attribute.
    var raw = card && card.getAttribute ? card.getAttribute('data-fitment') : null;
    var parsed = parseFitment(raw);

    if (parsed.kind === 'universal') return 'universal';
    if (parsed.kind === 'no_data')   return 'no_data';

    // No YMM picked → every card is shown (treat as universal-equivalent for filtering).
    if (!ymm) return 'universal';

    var _pf = ymmSlugPair(ymm, slugify);
    var pickedMake  = _pf[0];
    var pickedModel = _pf[1];
    var pickedYear  = Number(ymm.year);

    for (var i = 0; i < parsed.rows.length; i++) {
      var r = parsed.rows[i];
      if (r.make !== pickedMake) continue;
      if (r.model !== pickedModel) continue;
      if (pickedYear && (pickedYear < r.y1 || pickedYear > r.y2)) continue;
      return 'match';
    }
    return 'no_match';
  }

  // ─── grid filtering ──────────────────────────────────────────────────

  function filterGrid(opts) {
    opts = opts || {};
    var ymm = opts.ymm !== undefined ? opts.ymm : get();
    var cards = opts.cards || global.document.querySelectorAll('.gara-pcard');
    // hideNoData=true (default) hides cards whose fitment metafield is missing
    // or malformed — the strict-correct behavior. Set false for the legacy
    // "show everything" fallback if a widget needs it during transition.
    var hideNoData = opts.hideNoData !== false;

    var counts = {match: 0, universal: 0, no_data: 0, no_match: 0, shown: 0, hidden: 0};

    // If no YMM, show everything (no filtering applied).
    if (!ymm) {
      for (var k = 0; k < cards.length; k++) {
        cards[k].style.display = '';
        cards[k].classList.remove('gara-ymm-no-match', 'gara-ymm-no-data');
      }
      counts.shown = cards.length;
      return counts;
    }

    for (var i = 0; i < cards.length; i++) {
      var card = cards[i];
      var verdict = classify(card, ymm);
      counts[verdict]++;

      var show;
      if (verdict === 'match' || verdict === 'universal') show = true;
      else if (verdict === 'no_data') show = !hideNoData;
      else show = false;  // no_match

      card.style.display = show ? '' : 'none';
      card.classList.toggle('gara-ymm-no-match', verdict === 'no_match');
      card.classList.toggle('gara-ymm-no-data',  verdict === 'no_data');

      if (show) counts.shown++; else counts.hidden++;
    }
    return counts;
  }

  // ─── export ──────────────────────────────────────────────────────────

  global.GaraYMM = {
    get: get,
    set: set,
    clear: clear,
    subscribe: subscribe,
    classify: classify,
    classifyViaIndex: classifyViaIndex,    // exposed for cross-check / debugging
    filterGrid: filterGrid,
    canonicalizeModelSlug: canonicalizeModelSlug,   // exposed for bike-finder rewire
    resolveSizes: resolveSizes,            // bike → {front, rear, front_alt, rear_alt}
    getTireSizes: getTireSizes,            // current bike's stock sizes (or null)
    // Exposed for unit tests:
    _slugify: slugify,
    _parseFitment: parseFitment,
    _registrySlug: registrySlug,     // null means absent from the registry OR ambiguous
    _healStaleSlug: healStaleSlug,
  };

  // Repair the stored bike once all deferred data assets are loaded. Slug
  // first, because it decides what every grid on the page matches, then the
  // size backfill. A bike that is BOTH missing its slug and missing its
  // sizes gets written twice here, once by each repair, which is one extra
  // localStorage write on one page load. Both repairs are one-time.
  function initStorageRepairs() {
    healStaleSlug(get());
    backfillSizes();
  }
  if (global.document) {
    if (global.document.readyState !== 'loading') initStorageRepairs();
    else global.document.addEventListener('DOMContentLoaded', initStorageRepairs);
  }
})(typeof window !== 'undefined' ? window : this);
